/**
 * Global helper functions for checking user roles and permissions
 */

/**
 * Check if user has a specific role
 * @param {Object} user - User object from store/auth
 * @param {string} role - Role to check (e.g., 'admin', 'doctor', 'nurse', 'clinic')
 * @returns {boolean} - True if user has the specified role
 */
export const hasRole = (user, role) => {
  if (!user || !user.userType) return false;
  return user.userType === role;
};

/**
 * Get current user from localStorage
 * @returns {Object|null} - User object or null if not found
 */
export const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}User`);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

/**
 * Check if current user has a specific role
 * @param {string} role - Role to check
 * @returns {boolean} - True if current user has the specified role
 */
export const currentUserHasRole = (role) => {
  const user = getCurrentUser();
  return hasRole(user, role);
};

/**
 * Check if current user is a nurse
 * @returns {boolean} - True if current user is a nurse
 */
export const currentUserIsNurse = () => currentUserHasRole('nurse');

/**
 * Check if current user is a doctor
 * @returns {boolean} - True if current user is a doctor
 */
export const currentUserIsDoctor = () => currentUserHasRole('doctor');


/**
 * Check if current user is a basic
 * @returns {boolean} - True if current user is a basic
 */
export const currentUserIsBasic = () => currentUserHasRole('basic');


/**
 * Check if current user is an admin
 * @returns {boolean} - True if current user is an admin
 */
export const currentUserIsAdmin = () => currentUserHasRole('admin');

/**
 * Check if current user is an Super Admin
 * @returns {boolean} - True if current user is an admin
 */
export const currentUserIsSuperAdmin = () => currentUserHasRole('superAdmin');


/**
 * Get the current role of the logged-in user.
 * Value here is either "org-owner" or "staff".
 * 
 * @returns {string} - The role of the user.
 */
export function organizationRole() {
  return (
    localStorage.getItem(`${process.env.VUE_APP_ACCESS_TOKEN_NAME}Role`) || ''
  ).trim();
}
